import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sessions as SessionsAPI } from "../lib/api";
import { LocalStore } from "../lib/storage";
import { Camera as CameraIcon, FloppyDisk, Plus, Minus, MagicWand, CheckCircle, WarningCircle, Prohibit, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import VoiceMicButton from "../components/VoiceMicButton";
import { probeCamera, verdictFor } from "../lib/cameraProbe";
import {
  VoiceSupported,
  RecorderSupported,
  recordAndTranscribe,
  listenOnce,
  parseSessionSetupUtterance,
  parseTagList,
  speak,
} from "../lib/voice";

export default function SessionSetup() {
  const draft = LocalStore.getDraft();
  const [name, setName] = useState(draft.name || "");
  const [tags, setTags] = useState(draft.base_tags || "");
  const [seq, setSeq] = useState(draft.start_sequence ?? 1);
  const [saving, setSaving] = useState(false);
  const [voiceFlowing, setVoiceFlowing] = useState(false);
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);
  const nav = useNavigate();

  const runProbe = async () => {
    setProbing(true);
    try {
      const r = await probeCamera();
      setProbe(r);
    } finally {
      setProbing(false);
    }
  };

  const persistDraft = (next) => LocalStore.setDraft(next);

  const applyParsed = (parsed) => {
    let next = { name, base_tags: tags, start_sequence: seq };
    if (parsed.name) { setName(parsed.name); next.name = parsed.name; }
    if (parsed.base_tags) { setTags(parsed.base_tags); next.base_tags = parsed.base_tags; }
    if (parsed.start_sequence) { setSeq(parsed.start_sequence); next.start_sequence = parsed.start_sequence; }
    persistDraft({ ...draft, ...next });
    return next;
  };

  // Pattern 3: smart parse one utterance, then sequentially prompt for
  // anything still missing. Voice prompts honor LocalStore.getVoicePrompts().
  const fillByVoice = async () => {
    if (!VoiceSupported) {
      toast.error("VOICE NOT SUPPORTED IN THIS BROWSER");
      return;
    }
    const promptsOn = LocalStore.getVoicePrompts();
    setVoiceFlowing(true);
    try {
      await speak("Say session name, tags, and starting number.", { enabled: promptsOn });
      const first = await listenOnce({}).promise;
      if (!first) {
        toast("NO SPEECH DETECTED");
        return;
      }
      let next = applyParsed(parseSessionSetupUtterance(first));

      // Sequential fallback for any field still missing/empty
      if (!next.name?.trim()) {
        await speak("Session name?", { enabled: promptsOn });
        const t = await listenOnce({}).promise;
        if (t) next = applyParsed({ name: t });
      }
      if (!next.base_tags?.trim()) {
        await speak("Base tags?", { enabled: promptsOn });
        const t = await listenOnce({}).promise;
        if (t) next = applyParsed({ base_tags: parseTagList(t).join(", ") });
      }
      if (!next.start_sequence || next.start_sequence < 1) {
        await speak("Starting number?", { enabled: promptsOn });
        const t = await listenOnce({}).promise;
        const n = parseInt(String(t || "").replace(/[^\d]/g, ""), 10);
        if (Number.isFinite(n) && n >= 1) next = applyParsed({ start_sequence: n });
      }
      toast.success("VOICE FILL COMPLETE — REVIEW & SAVE");
    } catch (err) {
      const msg = String(err?.message || err);
      if (msg.includes("not-allowed") || msg.includes("denied")) {
        toast.error("MIC PERMISSION DENIED");
      } else {
        toast.error(`VOICE: ${msg.toUpperCase().slice(0, 80)}`);
      }
    } finally {
      setVoiceFlowing(false);
    }
  };

  const onChange = (setter, key) => (e) => {
    const v = e.target.value;
    setter(key === "start_sequence" ? Number(v || 0) : v);
    persistDraft({ ...draft, name, base_tags: tags, start_sequence: seq, [key]: key === "start_sequence" ? Number(v || 0) : v });
  };

  const create = async (goCamera) => {
    if (!name.trim()) return toast.error("Session name is required");
    if (!tags.trim()) return toast.error("At least one base tag is required");
    if (!Number.isFinite(seq) || seq < 1) return toast.error("Sequence must be ≥ 1");

    setSaving(true);
    try {
      const session = await SessionsAPI.create({
        name: name.trim(),
        base_tags: tags.trim(),
        start_sequence: seq,
      });
      LocalStore.setActiveSession(session);
      LocalStore.clearDraft();
      toast.success("Session created");
      nav(goCamera ? "/camera" : "/sessions");
    } catch (e) {
      toast.error("Failed to create session");
    } finally {
      setSaving(false);
    }
  };

  const tagChips = tags
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

  const adjustSeq = (delta) => {
    const next = Math.max(1, (seq || 1) + delta);
    setSeq(next);
    persistDraft({ ...draft, name, base_tags: tags, start_sequence: next });
  };

  return (
    <div className="px-4 py-6 max-w-2xl mx-auto page-fade" data-testid="session-setup-page">
      <p className="label-mono text-neutral-500">02 / SETUP</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
        NEW SESSION
      </h1>
      <p className="text-sm text-neutral-600 mt-2 mb-8 max-w-lg">
        Defines the auto-naming pattern and EXIF metadata for every photo in this batch.
      </p>

      {(RecorderSupported || VoiceSupported) && (
        <button
          type="button"
          onClick={fillByVoice}
          disabled={voiceFlowing || saving}
          className="btn-flat-outline w-full py-3 mb-6 inline-flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="voice-fill-all-btn"
          style={{ borderColor: voiceFlowing ? "#FF2A2A" : undefined }}
        >
          <MagicWand
            size={16}
            weight="bold"
            className={voiceFlowing ? "animate-pulse text-red-600" : ""}
          />
          {voiceFlowing ? "LISTENING…" : "FILL ALL BY VOICE"}
        </button>
      )}

      <div className="space-y-6">
        <Field
          label="Session Name"
          hint="e.g. eBay Batch 1"
          testid="session-name-input"
          value={name}
          onChange={onChange(setName, "name")}
          voice={
            <VoiceMicButton
              testId="voice-mic-name"
              onTranscript={(t) => {
                setName(t);
                persistDraft({ ...draft, name: t, base_tags: tags, start_sequence: seq });
              }}
            />
          }
        />
        <div>
          <Field
            label="Base Tags"
            hint="comma separated. e.g. Blue Jacket, XL. Voice: say tags with commas or “and”."
            testid="base-tags-input"
            value={tags}
            onChange={onChange(setTags, "base_tags")}
            voice={
              <VoiceMicButton
                testId="voice-mic-tags"
                onTranscript={(t) => {
                  const parsed = parseTagList(t).join(", ");
                  if (parsed) {
                    setTags(parsed);
                    persistDraft({ ...draft, name, base_tags: parsed, start_sequence: seq });
                  }
                }}
              />
            }
          />
          {tagChips.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="tag-chips-preview">
              {tagChips.map((t, i) => (
                <span key={`${t}-${i}`} className="chip">{t}</span>
              ))}
            </div>
          )}
        </div>

        <div>
          <span className="label-mono block mb-2">Starting Sequence</span>
          <div className="flex items-stretch border-2 border-black">
            <button
              type="button"
              onClick={() => adjustSeq(-1)}
              className="w-14 bg-black text-white flex items-center justify-center active:bg-neutral-800"
              data-testid="seq-decrement-btn"
              aria-label="Decrease sequence"
            >
              <Minus size={20} weight="bold" />
            </button>
            <input
              data-testid="start-sequence-input"
              type="number"
              min="1"
              value={seq}
              onChange={onChange(setSeq, "start_sequence")}
              className="flex-1 h-14 px-4 text-center text-2xl font-bold font-mono focus:outline-none focus:bg-neutral-50"
            />
            <button
              type="button"
              onClick={() => adjustSeq(1)}
              className="w-14 bg-black text-white flex items-center justify-center active:bg-neutral-800"
              data-testid="seq-increment-btn"
              aria-label="Increase sequence"
            >
              <Plus size={20} weight="bold" />
            </button>
          </div>
          <span className="block mt-2 text-xs text-neutral-500">
            zero-padded to 3 digits (001, 002, …)
          </span>
        </div>

        <div className="hairline-2 p-4 bg-neutral-50" data-testid="filename-preview">
          <p className="label-mono text-neutral-500 mb-1">Preview Filename</p>
          <p className="font-mono text-sm sm:text-base break-all">
            {previewName(name, tags, seq)}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button
            disabled={saving}
            onClick={() => create(true)}
            className="btn-flat btn-shutter flex-1 py-4 text-base"
            data-testid="create-and-capture-btn"
          >
            <CameraIcon weight="bold" size={20} />
            Create &amp; Capture
          </button>
          <button
            disabled={saving}
            onClick={() => create(false)}
            className="btn-flat-outline flex-1 py-4 text-base"
            data-testid="save-session-btn"
          >
            <FloppyDisk weight="bold" size={20} />
            Save Session
          </button>
        </div>

        {/* iter-37 — Camera Compatibility Probe. Answers "will my
            device work" BEFORE the user commits to a shoot. Not run
            automatically — some browsers red-flag unsolicited getUserMedia. */}
        <div className="border-2 border-black p-4 space-y-3" data-testid="camera-probe-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="label-mono text-xs text-neutral-500">CAMERA COMPATIBILITY</div>
              <div className="font-display font-bold text-lg">CHECK BEFORE YOU SHOOT</div>
              <div className="text-xs text-neutral-600 mt-1">
                Confirms your phone, webcam, or DSLR-substitute is reachable and
                permission is granted. Runs in ~2 seconds. No data leaves the browser.
              </div>
              <a
                href="/help/camera"
                onClick={(e) => { e.preventDefault(); nav("/help/camera"); }}
                className="label-mono text-[10px] text-neutral-500 hover:text-black underline mt-1 inline-block"
                data-testid="camera-probe-help-link"
              >
                SEE THE 8 COMMON PROBLEMS →
              </a>
            </div>
            <button
              type="button"
              onClick={runProbe}
              disabled={probing}
              className="btn-flat-outline text-xs shrink-0"
              data-testid="camera-probe-run"
            >
              {probing ? "TESTING…" : probe ? "RE-TEST" : "TEST NOW"}
            </button>
          </div>

          {probe && (
            <ProbeReport report={probe} onDismiss={() => setProbe(null)} />
          )}
        </div>
      </div>
    </div>
  );
}

function ProbeReport({ report, onDismiss }) {
  const verdict = verdictFor(report);
  const cls =
    verdict.level === "ok"
      ? "bg-green-50 border-green-600"
      : verdict.level === "warn"
      ? "bg-yellow-50 border-yellow-600"
      : "bg-red-50 border-red-600";
  const Icon =
    verdict.level === "ok" ? CheckCircle : verdict.level === "warn" ? WarningCircle : Prohibit;
  return (
    <div className={`border-2 p-3 relative ${cls}`} data-testid={`camera-probe-report-${verdict.level}`}>
      <button
        onClick={onDismiss}
        className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center"
        aria-label="Dismiss"
        data-testid="camera-probe-dismiss"
      >
        <X size={12} weight="bold" />
      </button>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={20} weight="bold" />
        <span className="font-display font-bold tracking-tighter text-lg" data-testid="camera-probe-verdict">
          {verdict.label}
        </span>
      </div>
      <ul className="text-xs font-mono space-y-1">
        <li className="flex justify-between">
          <span>HTTPS</span>
          <span>{report.https ? "OK" : "MISSING"}</span>
        </li>
        <li className="flex justify-between">
          <span>BROWSER API</span>
          <span>{report.api ? "OK" : "MISSING"}</span>
        </li>
        <li className="flex justify-between">
          <span>PERMISSION</span>
          <span>{(report.permission || "unknown").toUpperCase()}</span>
        </li>
        <li className="flex justify-between">
          <span>CAMERAS FOUND</span>
          <span data-testid="camera-probe-count">{report.cameras}</span>
        </li>
        {report.winner && (
          <>
            <li className="flex justify-between">
              <span>PICKED</span>
              <span className="truncate max-w-[180px]" title={report.winner.label || ""}>{report.winner.label || "default"}</span>
            </li>
            <li className="flex justify-between">
              <span>RESOLUTION</span>
              <span>
                {report.winner.width && report.winner.height
                  ? `${report.winner.width}x${report.winner.height}`
                  : "unknown"}
              </span>
            </li>
          </>
        )}
      </ul>
      {report.tips.length > 0 && (
        <div className="mt-2 pt-2 border-t border-black/20 text-xs" data-testid="camera-probe-tips">
          <div className="label-mono text-[10px] text-neutral-600 mb-1">TIPS</div>
          <ul className="list-disc list-inside space-y-1">
            {report.tips.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function previewName(name, tags, seq) {
  const clean = (s) =>
    (s || "")
      .toString()
      .trim()
      .replace(/[^a-zA-Z0-9-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  const n = clean(name) || "session";
  const t = clean(tags) || "untagged";
  return `${n}_${t}_${String(seq || 1).padStart(3, "0")}.jpg`;
}

function Field({ label, hint, testid, voice, ...props }) {
  return (
    <label className="block">
      <span className="label-mono block mb-2">{label}</span>
      <div className="flex items-stretch gap-2">
        <input
          {...props}
          data-testid={testid}
          className="flex-1 h-14 border-2 border-black bg-white px-4 text-lg font-medium focus:outline-none focus:ring-4 focus:ring-[#002FA7] focus:ring-offset-0"
        />
        {voice && (
          <div className="flex items-center justify-center" style={{ width: 56, height: 56 }}>
            {voice}
          </div>
        )}
      </div>
      {hint && <span className="block mt-2 text-xs text-neutral-500">{hint}</span>}
    </label>
  );
}
